import React from 'react';

interface GridProps {
  container?: boolean;
  size?: number | { xs?: number; sm?: number; md?: number; lg?: number; xl?: number };
  spacing?: number;
  rowSpacing?: number;
  columnSpacing?: number | { xs?: number; sm?: number; md?: number };
  display?: string;
  justifyContent?: string;
  alignItems?: string;
  py?: number;
  px?: number | { md?: number; xs?: number; sm?: number };
  p?: number | { md?: number };
  pr?: number;
  mt?: number;
  borderBottom?: string;
  borderRadius?: number;
  bgcolor?: string;
  color?: string;
  sx?: React.CSSProperties & { [key: string]: any };
  className?: string;
  children?: React.ReactNode;
  key?: any;
}

const Grid: React.FC<GridProps> = ({
  container = false,
  size,
  spacing = 0,
  rowSpacing,
  columnSpacing,
  display,
  justifyContent,
  alignItems,
  py,
  px,
  p,
  pr,
  mt,
  borderBottom,
  borderRadius,
  bgcolor,
  color,
  sx,
  className = '',
  children,
  key,
}) => {
  const style: React.CSSProperties = {
    ...(px !== undefined && typeof px === 'number' ? { paddingLeft: `${px * 0.25}rem`, paddingRight: `${px * 0.25}rem` } : {}),
    ...(px !== undefined && typeof px === 'object' ? (() => {
      const pxStyle: React.CSSProperties = {};
      if (px.md !== undefined) {
        pxStyle.paddingLeft = `${px.md * 0.25}rem`;
        pxStyle.paddingRight = `${px.md * 0.25}rem`;
      }
      if (px.xs !== undefined) {
        pxStyle.paddingLeft = `${px.xs * 0.25}rem`;
        pxStyle.paddingRight = `${px.xs * 0.25}rem`;
      }
      return pxStyle;
    })() : {}),
    ...(pr !== undefined && { paddingRight: `${pr * 0.25}rem` }),
    ...(mt !== undefined && { marginTop: `${mt * 0.25}rem` }),
    ...(borderBottom && { borderBottom }),
    ...(borderRadius !== undefined && { borderRadius: `${borderRadius * 0.25}rem` }),
    ...(bgcolor && { backgroundColor: bgcolor }),
    ...(color && { color }),
    ...(sx && typeof sx === 'object' && !Array.isArray(sx) ? sx : {}),
  };

  if (container) {
    const spacingClass = spacing > 0 ? `gap-${spacing}` : '';
    const rowSpacingClass = rowSpacing ? `gap-y-${rowSpacing}` : '';
    const colSpacingClass = columnSpacing 
      ? (typeof columnSpacing === 'number' 
          ? `gap-x-${columnSpacing}` 
          : Object.entries(columnSpacing).map(([breakpoint, val]) => `${breakpoint}:gap-x-${val}`).join(' '))
      : '';
    
    const paddingClass = p 
      ? (typeof p === 'number' 
          ? `p-${p}` 
          : Object.entries(p).map(([breakpoint, val]) => `${breakpoint}:p-${val}`).join(' '))
      : '';
    
    return (
      <div key={key} className={`grid grid-cols-12 ${spacingClass} ${rowSpacingClass} ${colSpacingClass} ${paddingClass} ${className}`} style={style}>
        {children}
      </div>
    );
  }

  const sizeClasses = size
    ? typeof size === 'number'
      ? `col-span-${size}`
      : Object.entries(size)
          .map(([breakpoint, val]) => {
            const breakpointMap: Record<string, string> = {
              xs: '',
              sm: 'sm:',
              md: 'md:',
              lg: 'lg:',
              xl: 'xl:',
            };
            return `${breakpointMap[breakpoint] || ''}col-span-${val}`;
          })
          .join(' ')
    : '';

  const displayClass = display ? `flex` : '';
  const justifyContentClass = justifyContent ? `justify-${justifyContent === 'center' ? 'center' : justifyContent === 'end' ? 'end' : 'start'}` : '';
  const alignItemsClass = alignItems ? `items-${alignItems === 'start' ? 'start' : alignItems === 'center' ? 'center' : 'end'}` : '';
  const pyClass = py ? `py-${py}` : '';

  return (
    <div key={key} className={`${sizeClasses} ${displayClass} ${justifyContentClass} ${alignItemsClass} ${pyClass} ${className}`} style={style}>
      {children}
    </div>
  );
};

export default Grid;

